import React from "react";
import { connect } from "react-redux";

// MUI Components
import Button from "@material-ui/core/Button";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import Typography from "@material-ui/core/Typography";
import Divider from "@material-ui/core/Divider";
import { makeStyles } from "@material-ui/core/styles";

// Icons
import { TargetIcon, ThicknessIcon } from "../../constants/customIcons";
import CircularProgress from "@material-ui/core/CircularProgress";
import KeyboardArrowRight from "@material-ui/icons/KeyboardArrowRight";
import KeyboardArrowLeft from "@material-ui/icons/KeyboardArrowLeft";

import BottomValueDisplay from "../Samples/BottomValueDisplay";
import { handleNullValues } from "../../constants/utilityFunctions";
import { colors } from "../../constants/colors";
import { customTheme } from "../../constants/customTheme";
import classNames from "classnames";

const useStyles = makeStyles({
  root: (props) => ({
    ...customTheme(props.theme).dialog.root,
    minWidth: "650px",
    overflow: "hidden",
  }),
  title: customTheme().dialog.title,
  contentContainer: {
    display: "flex",
    flexDirection: "column",
    overflowY: "unset",
    alignItems: "center",
    width: "100%",
  },
  autoAdvanceText: {
    fontSize: "1.1.rem",
    textAlign: "center",
  },
  divider: {
    width: "80%",
    backgroundColor: (props) => colors[props.theme].primary,
  },
  BottomValueDisplayContainer: {
    marginTop: "10px",
    display: "flex",
    flexDirection: "row",
    width: "100%",
  },
  buttonContainer: {
    ...customTheme().dialog.buttonContainer,
    paddingTop: "15px",
    justifyContent: "center",
  },
  countdown: {
    textAlign: "center",
    marginLeft: "20px",
    marginRight: "20px",
    position: "relative",
  },
  seconds: {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    textAlign: "center",
    fontSize: "2.0rem",
  },
  autoAdvanceButton: {
    fontSize: "1.1rem",
    fontWeight: 400,
  },
  iconMedium: customTheme().iconMedium,
});

const AutoAdvanceDialog = (props) => {
  const classes = useStyles(props);

  return (
    <Dialog
      open={props.open}
      PaperProps={{ className: classNames(classes.root) }}
      onEnter={props.resetAutoAdvanceTimeout}
      onEntered={props.handleAutoAdvanceTimeout}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
    >
      <Typography
        className={classes.title}
        variant="h4"
        color="textPrimary"
        align="center"
      >
        Advancing to: {props.nextAutoStepName}
      </Typography>
      <DialogContent className={classes.contentContainer}>
        {/* Display SWS and Thickness Results For The Location */}
        <Typography variant="h6" className={classes.autoAdvanceText}>
          {props.selectedLocationName} Completed
        </Typography>
        <Divider className={classes.divider} />
        <div className={classes.BottomValueDisplayContainer}>
          {props.locationSwsValue && (
            <BottomValueDisplay
              icon={<TargetIcon />}
              title="Shear Wave Speed"
              units="m/s"
              value={handleNullValues(props.locationSwsValue)}
              standardDev={handleNullValues(props.locationSwsSD)}
              sampleCount={props.samplesAcquired["CSI"]}
              color={colors[props.theme].primary}
              small={false}
              theme={props.theme}
            />
          )}
          {props.locationThicknessValue && (
            <BottomValueDisplay
              icon={<ThicknessIcon />}
              title="Skin Thickness"
              units="mm"
              value={handleNullValues(props.locationThicknessValue)}
              standardDev={handleNullValues(props.locationThicknessSD)}
              sampleCount={props.samplesAcquired["BMODE"]}
              color={colors[props.theme].secondary}
              small={false}
              theme={props.theme}
            />
          )}
        </div>
      </DialogContent>
      <DialogActions className={classes.buttonContainer} disableSpacing={true}>
        <Button
          className={classes.autoAdvanceButton}
          onClick={() => props.handleAutoAdvanceDismiss()}
        >
          <KeyboardArrowLeft className={classes.iconMedium} />
          Cancel
        </Button>
        <div className={classes.countdown}>
          <CircularProgress
            size={70}
            variant="static"
            value={(props.seconds / props.totalSeconds) * 100}
            className={classes.progress}
            style={{ transform: "scaleX(-1) rotate(-90deg)" }}
          />
          <div className={classes.seconds}>{Math.ceil(props.seconds)}</div>
        </div>
        <Button
          onClick={() => props.handleAdvance()}
          color="primary"
          className={classes.autoAdvanceButton}
        >
          Advance
          <KeyboardArrowRight className={classes.iconMedium} />
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const mapStateToProps = (state) => {
  let selectedLocationId = state.locations.selectedLocationId;
  return {
    samplesAcquired:
      selectedLocationId === null
        ? null
        : state.locations.byId[selectedLocationId].samplesAcquired,
    locationThicknessValue: selectedLocationId
      ? state.locations.byId[state.locations.selectedLocationId].thickness.value
      : null,
    locationThicknessSD: selectedLocationId
      ? state.locations.byId[state.locations.selectedLocationId].thickness
          .standardDev
      : null,
    locationSwsValue: selectedLocationId
      ? state.locations.byId[state.locations.selectedLocationId].shearWaveSpeed
          .value
      : null,
    locationSwsSD: selectedLocationId
      ? state.locations.byId[state.locations.selectedLocationId].shearWaveSpeed
          .standardDev
      : null,
    nextAutoStepName:
      state.locations.nextAutoStep.form === "LOCATION"
        ? state.locations.byId[state.locations.nextAutoStep.locationId].name
        : state.locations.nextAutoStep.locationId,
    selectedLocationName: selectedLocationId
      ? state.locations.byId[state.locations.selectedLocationId].name
      : null,
    theme: state.utility.theme,
  };
};

export default connect(mapStateToProps)(AutoAdvanceDialog);
